import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CreditCard, Truck, Gift } from "lucide-react";
import api, { money, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Checkout() {
  const { items, subtotal, discount } = useCart();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", address: "", city: "", zip: "", country: "", phone: "" });
  const [delivery, setDelivery] = useState("standard");
  const [gift, setGift] = useState("");

  const shipping = subtotal - discount >= 150 ? 0 : 15;
  const expressFee = delivery === "express" ? 25 : 0;
  const total = subtotal - discount + shipping + expressFee;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    for (const k of ["full_name", "email", "address", "city", "zip", "country"]) {
      if (!form[k].trim()) { toast.error("Please complete all shipping fields"); return false; }
    }
    return true;
  };

  const payload = () => ({
    items, origin_url: window.location.origin, shipping_address: form,
    gift_message: gift || null, delivery_method: delivery,
  });

  const payStripe = async () => {
    if (items.length === 0) { toast.error("Your bag is empty"); return; }
    if (!validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", payload());
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] pt-32 flex flex-col items-center justify-center gap-4">
        <p className="font-serif-lux text-3xl text-[#FAFAF7]/70">Your bag is empty</p>
        <button onClick={() => navigate("/shop")} className="btn-gold px-8 py-3 rounded-full text-sm">Discover Fragrances</button>
      </div>
    );
  }

  return (
    <div className="bg-[#0B0B0C] min-h-screen pt-28 sm:pt-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <h1 className="font-serif-lux text-4xl sm:text-5xl font-light text-[#FAFAF7] mb-10">Checkout</h1>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
          {/* Form */}
          <div className="space-y-8">
            <div>
              <p className="eyebrow mb-4 flex items-center gap-2"><Truck size={14} className="text-[#D4AF37]" /> Shipping Address</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="checkout-name" placeholder="Full name" value={form.full_name} onChange={set("full_name")} className="input-lux sm:col-span-2" />
                <input data-testid="checkout-email" type="email" placeholder="Email" value={form.email} onChange={set("email")} className="input-lux" />
                <input data-testid="checkout-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className="input-lux" />
                <input data-testid="checkout-address" placeholder="Street address" value={form.address} onChange={set("address")} className="input-lux sm:col-span-2" />
                <input data-testid="checkout-city" placeholder="City" value={form.city} onChange={set("city")} className="input-lux" />
                <input data-testid="checkout-zip" placeholder="ZIP code" value={form.zip} onChange={set("zip")} className="input-lux" />
                <input data-testid="checkout-country" placeholder="Country" value={form.country} onChange={set("country")} className="input-lux sm:col-span-2" />
              </div>
            </div>

            <div>
              <p className="eyebrow mb-4 flex items-center gap-2"><Truck size={14} className="text-[#D4AF37]" /> Delivery Method</p>
              <div className="space-y-3">
                {[["standard", "Standard Delivery", subtotal >= 150 ? "Free" : money(15), "3-5 business days"], ["express", "Express Delivery", money(25), "1-2 business days"]].map(([id, name, price, sub]) => (
                  <button key={id} data-testid={`delivery-${id}`} onClick={() => setDelivery(id)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-lg border text-left transition-all ${delivery === id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[rgba(212,175,55,0.2)]"}`}>
                    <div><p className="text-sm text-[#FAFAF7]">{name}</p><p className="text-xs text-[#FAFAF7]/50">{sub}</p></div>
                    <span className="text-[#D4AF37] text-sm">{price}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="eyebrow mb-4 flex items-center gap-2"><Gift size={14} className="text-[#D4AF37]" /> Gift Message (optional)</p>
              <textarea data-testid="checkout-gift" value={gift} onChange={(e) => setGift(e.target.value)} rows={2} placeholder="Add a personal note…" className="input-lux w-full resize-none" />
            </div>
          </div>

          {/* Summary */}
          <div className="lg:sticky lg:top-28 h-fit">
            <div className="gold-border rounded-xl bg-[#141416] p-6">
              <h3 className="font-serif-lux text-2xl text-[#FAFAF7] mb-5">Order Summary</h3>
              <div className="space-y-3 max-h-56 overflow-y-auto mb-4">
                {items.map((it, i) => (
                  <div key={i} className="flex gap-3">
                    <img src={it.image} alt={it.name} className="w-12 h-14 object-cover rounded" />
                    <div className="flex-1">
                      <p className="text-sm text-[#FAFAF7] leading-tight">{it.name}</p>
                      <p className="text-xs text-[#FAFAF7]/50">{it.volume} · ×{it.quantity}</p>
                    </div>
                    <span className="text-sm text-[#D4AF37]">{money(it.price * it.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2 py-4 border-t border-[rgba(212,175,55,0.15)] text-sm">
                <div className="flex justify-between text-[#FAFAF7]/70"><span>Subtotal</span><span>{money(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-400"><span>Air Fresheners · 3 for $25</span><span data-testid="checkout-discount">−{money(discount)}</span></div>}
                <div className="flex justify-between text-[#FAFAF7]/70"><span>Shipping</span><span>{shipping === 0 ? "Free" : money(shipping)}</span></div>
                {expressFee > 0 && <div className="flex justify-between text-[#FAFAF7]/70"><span>Express</span><span>{money(expressFee)}</span></div>}
              </div>
              <div className="flex justify-between items-center py-4 border-t border-[rgba(212,175,55,0.15)]">
                <span className="text-[#FAFAF7]">Total</span>
                <span className="font-serif-lux text-2xl text-[#D4AF37]" data-testid="checkout-total">{money(total)}</span>
              </div>
              <button data-testid="pay-stripe-button" onClick={payStripe} disabled={busy} className="btn-gold w-full py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                <CreditCard size={16} /> {busy ? "Redirecting to secure payment…" : `Pay ${money(total)} Securely`}
              </button>
              <p className="text-xs text-[#FAFAF7]/40 flex items-center justify-center gap-1.5 mt-4"><Lock size={12} /> Secure checkout powered by Stripe · Visa, Mastercard, Amex, Apple Pay</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
